import React, { useState, useEffect } from 'react';
import {
    BarChart3,
    AlertTriangle,
    RefreshCw,
    Download,
    Clock,
    TrendingUp,
    FileText,
    Copy
} from 'lucide-react';
import WeightEditor from './WeightEditor';
import ComputationDetails from './ComputationDetails';
import D3TrendChart from './D3TrendChart';
import EquipmentList from './EquipmentList';
import CompanyInfoForm from './CompanyInfoForm';
import WorkflowVisualization from './WorkflowVisualization';
import {
    ClientDetailsProps,
    CreditScore,
    CategoryWeights
} from '../types';
import { getDefaultCategoryWeights, recalculateClient, getClientTrends, downloadExcelReport, downloadDocumentsPackage, nextStage, getParsedDocuments, ParsedDocument, getClientLoans } from '../services/api';
import { generateEmailTemplate } from '../utils/emailTemplate';
import EquipmentSelector from './EquipmentSelector';
import UnifiedFileUpload from './UnifiedFileUpload';
import DocumentUpload from './DocumentUpload';
import { WORKFLOW_BUTTON_TEXT, shouldShowWorkflowButton } from '../config/workflow';

const ClientDetails: React.FC<ClientDetailsProps> = ({
    client,
    companyInfo,
    onCompanyInfoChange,
    onEvaluate,
    onStartNewClient,
    onReset,
    isLoading,
    error,
    canEvaluate,
    isNewClient,
    currentClientId,
    onClientUpdate
}) => {
    const [activeTab, setActiveTab] = useState<'computation' | 'trends' | 'source data' | 'equipment' | 'documents'>('trends');
    const [currentWeights, setCurrentWeights] = useState<CategoryWeights | null>(null);
    const [originalWeights, setOriginalWeights] = useState<CategoryWeights | null>(null);
    const [isRecalculating, setIsRecalculating] = useState<boolean>(false);
    const [focusedComputationCategory, setFocusedComputationCategory] = useState<string | null>(null);
    const [selectedRatio, setSelectedRatio] = useState<string>('current_ratio');
    const [trendData, setTrendData] = useState<Array<{ year: number, value: number }>>([]);
    const [isLoadingTrends, setIsLoadingTrends] = useState<boolean>(false);
    const [parsedDocuments, setParsedDocuments] = useState<{
        equifax: ParsedDocument[];
        paynet: ParsedDocument[];
        id: ParsedDocument[];
    }>({
        equifax: [],
        paynet: [],
        id: []
    });
    const [isLoadingParsedDocuments, setIsLoadingParsedDocuments] = useState<boolean>(false);

    // Create refs for each computation category
    const computationRefs = React.useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

    const [creditScore, setCreditScore] = useState<CreditScore | null>(
        client?.results?.credit_score as CreditScore | null
    );

    // Initialize weights from result or fetch defaults
    useEffect(() => {
        if (client?.results?.category_weights) {
            setCurrentWeights(client.results.category_weights);
            setOriginalWeights(client.results.category_weights);
        } else {
            // Fetch default weights if not available
            getDefaultCategoryWeights()
                .then(response => {
                    if (response.success) {
                        setCurrentWeights(response.weights);
                        setOriginalWeights(response.weights);
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch default weights:', error);
                });
        }
    }, [client?.results?.category_weights]);

    // Update credit score when client changes
    useEffect(() => {
        if (client?.results?.credit_score) {
            setCreditScore(client.results.credit_score);
        } else {
            setCreditScore(null);
        }
    }, [client?.results?.credit_score]);

    const handleWeightsChange = (weights: CategoryWeights) => {
        setCurrentWeights(weights);
    };

    const handleResetSingleWeight = (category: keyof CategoryWeights) => {
        if (!originalWeights) return;

        const newWeights = {
            ...currentWeights!,
            [category]: originalWeights[category]
        };
        setCurrentWeights(newWeights);
    };

    const isWeightModified = (category: keyof CategoryWeights): boolean => {
        if (!currentWeights || !originalWeights) return false;
        return Math.abs(currentWeights[category] - originalWeights[category]) > 0.001;
    };

    const hasAnyModifiedWeights = (): boolean => {
        if (!currentWeights || !originalWeights) return false;
        return Object.keys(currentWeights).some(key =>
            isWeightModified(key as keyof CategoryWeights)
        );
    };

    const handleApplyWeights = async () => {
        if (!currentWeights || !client?.id) return;

        setIsRecalculating(true);
        try {
            const response = await recalculateClient(client.id, currentWeights);
            if (response.success && response.data) {
                // Update original weights to current weights after successful recalculation
                setOriginalWeights(currentWeights);
                setCreditScore(response.data.credit_score as CreditScore);
            }
        } catch (error) {
            console.error('Failed to recalculate client:', error);
        } finally {
            setIsRecalculating(false);
        }
    };

    const getRatingColor = (rating: string): string => {
        const colors: Record<string, string> = {
            'AAA': '#10b981',
            'AA': '#34d399',
            'A': '#6ee7b7',
            'BBB': '#fbbf24',
            'BB': '#f59e0b',
            'B': '#f97316',
            'CCC': '#ef4444',
            'CC': '#dc2626',
            'C': '#b91c1c',
            'D': '#7f1d1d'
        };
        return colors[rating] || '#6b7280';
    };

    // Fetch trend data from backend when trends tab is active
    useEffect(() => {
        if (activeTab === 'trends' && client?.id) {
            setIsLoadingTrends(true);
            getClientTrends(client.id)
                .then(response => {
                    if (response.success && response.data) {
                        // Map backend trend data to chart format for selected ratio
                        const mappedData = response.data
                            .map(item => ({
                                year: item.year,
                                value: item.ratios[selectedRatio] || 0
                            }))
                            .filter(item => item.value !== 0)
                            .sort((a, b) => a.year - b.year);
                        setTrendData(mappedData);
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch trends:', error);
                    setTrendData([]);
                })
                .finally(() => {
                    setIsLoadingTrends(false);
                });
        }
    }, [activeTab, client?.id, selectedRatio]);

    // Fetch parsed documents when documents tab is active
    useEffect(() => {
        if (activeTab === 'documents' && client?.id) {
            setIsLoadingParsedDocuments(true);
            getParsedDocuments(client.id)
                .then(response => {
                    if (response.success && response.parsed_documents) {
                        setParsedDocuments(response.parsed_documents);
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch parsed documents:', error);
                })
                .finally(() => {
                    setIsLoadingParsedDocuments(false);
                });
        }
    }, [activeTab, client?.id]);

    // Available ratios for selection
    const availableRatios = [
        { key: 'current_ratio', label: 'Current Ratio' },
        { key: 'quick_ratio', label: 'Quick Ratio' },
        { key: 'cash_ratio', label: 'Cash Ratio' },
        { key: 'gross_profit_margin', label: 'Gross Profit Margin' },
        { key: 'operating_margin', label: 'Operating Margin' },
        { key: 'net_profit_margin', label: 'Net Profit Margin' },
        { key: 'return_on_assets', label: 'Return on Assets' },
        { key: 'return_on_equity', label: 'Return on Equity' },
        { key: 'debt_to_equity', label: 'Debt to Equity' },
        { key: 'debt_to_assets', label: 'Debt to Assets' },
        { key: 'interest_coverage', label: 'Interest Coverage' },
        { key: 'debt_service_coverage', label: 'Debt Service Coverage' },
        { key: 'asset_turnover', label: 'Asset Turnover' },
        { key: 'inventory_turnover', label: 'Inventory Turnover' },
        { key: 'receivables_turnover', label: 'Receivables Turnover' },
        { key: 'payables_turnover', label: 'Payables Turnover' },
        { key: 'operating_cash_flow_ratio', label: 'Operating Cash Flow Ratio' },
        { key: 'free_cash_flow', label: 'Free Cash Flow' },
        { key: 'cash_flow_coverage', label: 'Cash Flow Coverage' }
    ];

    const generateReport = async (): Promise<void> => {
        if (!client?.id) return;
        try {
            await downloadExcelReport(client.id);
        } catch (error) {
            console.error('Error downloading Excel report:', error);
            // Fall back to show an error message to the user
            alert('Failed to download the Excel report. Please try again.');
        }
    };

    const downloadDocuments = async (): Promise<void> => {
        if (!client?.id) return;
        try {
            await downloadDocumentsPackage(client.id);
        } catch (error) {
            console.error('Error downloading documents package:', error);
            // Fall back to show an error message to the user
            alert('Failed to download the documents package. Please try again.');
        }
    };

    const copyEmailTemplate = async (): Promise<void> => {
        if (!client?.id) return;
        try {
            // Fetch loans data
            const loansResponse = await getClientLoans(client.id);
            const loans = loansResponse.success && loansResponse.loans ? loansResponse.loans : [];

            // Generate email template
            const emailTemplate = generateEmailTemplate(client, loans, creditScore);

            // Copy to clipboard
            await navigator.clipboard.writeText(emailTemplate);

            // Show success message
            alert('Email template copied to clipboard!');
        } catch (error) {
            console.error('Error copying email template:', error);
            alert('Failed to copy email template. Please try again.');
        }
    };

    const handleNextStage = async (): Promise<void> => {
        if (!client?.id) return;
        try {
            setIsRecalculating(true);
            const response = await nextStage(client.id);
            if (response.success && response.client) {
                // Update client via callback if provided
                if (onClientUpdate) {
                    onClientUpdate(response.client);
                }
            }
        } catch (error) {
            console.error('Failed to progress workflow:', error);
            alert('Failed to progress workflow. Please try again.');
        } finally {
            setIsRecalculating(false);
        }
    };

    // Show form view for draft/new clients
    if (client?.status === 'draft' || isNewClient) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            {client?.company_name || 'Company Credit Evaluation System'}
                        </h1>
                        <p className="text-lg text-gray-600 mt-1">
                            {client?.industry
                                ? client.industry.charAt(0).toUpperCase() + client.industry.slice(1)
                                : isNewClient
                                    ? 'Upload your financial statements and get a comprehensive credit analysis'
                                    : 'Continue Evaluation'}
                        </p>
                    </div>
                    <button
                        onClick={onReset}
                        className="btn btn-secondary"
                        type="button"
                    >
                        Back to List
                    </button>
                </div>

                {/* Workflow Visualization */}
                {client && <WorkflowVisualization clientStatus={client.status} />}

                {/* Workflow Progression Button */}
                {client && shouldShowWorkflowButton(client.status) && (
                    <div className="text-center">
                        <button
                            className="btn btn-primary"
                            onClick={handleNextStage}
                            disabled={isRecalculating}
                            type="button"
                        >
                            {isRecalculating ? (
                                <>
                                    <div className="spinner"></div>
                                    Progressing...
                                </>
                            ) : (
                                WORKFLOW_BUTTON_TEXT[client.status]
                            )}
                        </button>
                    </div>
                )}

                <CompanyInfoForm
                    companyInfo={companyInfo}
                    onInfoChange={onCompanyInfoChange}
                    disabled={!isNewClient && !!currentClientId}
                />

                {/* Equipment Selector */}
                {(currentClientId || client?.id) && (
                    <div className="card">
                        <EquipmentSelector clientId={currentClientId || client?.id} />
                    </div>
                )}

                {/* Create client button for new clients */}
                {isNewClient && companyInfo.companyName && (
                    <div className="text-center">
                        <button
                            className="btn btn-secondary"
                            onClick={onStartNewClient}
                            disabled={isLoading}
                            type="button"
                        >
                            {isLoading ? 'Creating Client...' : 'Create Client & Continue'}
                        </button>
                    </div>
                )}

                {(currentClientId || client?.id) && (
                    <>
                        <UnifiedFileUpload
                            clientId={currentClientId || client?.id}
                        />

                        <DocumentUpload
                            clientId={currentClientId || client?.id}
                        />

                        {error && (
                            <div className="alert alert-error">
                                <strong>Error:</strong> {error}
                            </div>
                        )}

                        <div className="text-center">
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={onEvaluate}
                                disabled={!canEvaluate || isLoading}
                                type="button"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="spinner"></div>
                                        Evaluating...
                                    </>
                                ) : (
                                    'Evaluate Credit'
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Show results view for completed clients
    if (!client || !creditScore) {
        return null;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{client.company_name || 'Company'}</h1>
                    <p className="text-lg text-gray-600 mt-1">
                        {client.industry ? client.industry.charAt(0).toUpperCase() + client.industry.slice(1) : 'Unknown Industry'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={generateReport}
                        className="btn btn-secondary"
                        type="button"
                    >
                        <Download className="w-4 h-4" />
                        Download Excel Report
                    </button>
                    <button
                        onClick={downloadDocuments}
                        className="btn btn-secondary"
                        type="button"
                    >
                        <FileText className="w-4 h-4" />
                        Download Documents Package
                    </button>
                    <button
                        onClick={copyEmailTemplate}
                        className="btn btn-secondary"
                        type="button"
                    >
                        <Copy className="w-4 h-4" />
                        Copy Email Template
                    </button>
                    <button
                        onClick={onReset}
                        className="btn btn-secondary"
                        type="button"
                    >
                        Back to List
                    </button>
                </div>
            </div>

            {/* Workflow Visualization */}
            <WorkflowVisualization clientStatus={client.status} />

            {/* Workflow Progression Button */}
            {shouldShowWorkflowButton(client.status) && (
                <div className="text-center mb-6">
                    <button
                        className="btn btn-primary"
                        onClick={handleNextStage}
                        disabled={isRecalculating}
                        type="button"
                    >
                        {isRecalculating ? (
                            <>
                                <div className="spinner"></div>
                                Progressing...
                            </>
                        ) : (
                            WORKFLOW_BUTTON_TEXT[client.status]
                        )}
                    </button>
                </div>
            )}

            {/* Main Score Card */}
            <div className="card">
                <div className="text-center">
                    <div className="mb-6">
                        <div
                            className="inline-flex items-center justify-center w-32 h-32 rounded-full text-white text-4xl font-bold"
                            style={{ backgroundColor: getRatingColor(creditScore.rating) }}
                        >
                            {creditScore.rating}
                        </div>
                        <div className="mt-4">
                            <h2 className="text-2xl font-bold text-gray-900">Credit Rating: {creditScore.rating}</h2>
                            <p className="text-lg text-gray-600">Score: {creditScore.overall_score.toFixed(0)}/100</p>
                            <p className="text-sm text-gray-500">Data Quality: {creditScore.confidence_level}</p>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
                        {Object.entries(creditScore.breakdown).map(([category, score]) => {
                            const weight = currentWeights?.[category as keyof CategoryWeights] || 0;
                            const categoryColors = {
                                liquidity: { bg: 'bg-blue-50', text: 'text-blue-900', icon: 'text-blue-600' },
                                profitability: { bg: 'bg-green-50', text: 'text-green-900', icon: 'text-green-600' },
                                leverage: { bg: 'bg-red-50', text: 'text-red-900', icon: 'text-red-600' },
                                efficiency: { bg: 'bg-orange-50', text: 'text-orange-900', icon: 'text-orange-600' },
                                cash_flow: { bg: 'bg-purple-50', text: 'text-purple-900', icon: 'text-purple-600' }
                            };
                            const colors = categoryColors[category as keyof typeof categoryColors] || categoryColors.liquidity;

                            return (
                                <div
                                    key={category}
                                    className={`${colors.bg} p-4 rounded-lg relative group cursor-pointer transition-all hover:shadow-md`}
                                    onClick={() => {
                                        if (creditScore.computation_details?.[category]) {
                                            setActiveTab('computation');
                                            setFocusedComputationCategory(category);
                                        }
                                    }}
                                >
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                        <BarChart3 className={`w-5 h-5 ${colors.icon}`} />
                                        <h3 className={`font-semibold ${colors.text} capitalize`}>
                                            {category.replace('_', ' ')}
                                        </h3>
                                        {creditScore.computation_details?.[category] && (
                                            <div className={`text-xs ${colors.text} opacity-70 ml-1`}>
                                                📊
                                            </div>
                                        )}
                                    </div>
                                    <p className={`text-2xl font-bold ${colors.text} mb-2`}>
                                        {score.toFixed(0)}/100
                                    </p>

                                    {creditScore.computation_details?.[category] && (
                                        <div className={`text-xs ${colors.text} opacity-70 text-center mb-2`}>
                                            Click for details
                                        </div>
                                    )}

                                    {/* Weight Display/Edit */}
                                    <WeightEditor
                                        category={category}
                                        weight={weight}
                                        isModified={isWeightModified(category as keyof CategoryWeights)}
                                        isRecalculating={isRecalculating}
                                        colors={colors}
                                        onWeightChange={(newWeight) => {
                                            const newWeights = {
                                                ...currentWeights!,
                                                [category]: newWeight
                                            };
                                            handleWeightsChange(newWeights);
                                        }}
                                        onResetWeight={() => handleResetSingleWeight(category as keyof CategoryWeights)}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Weight Summary */}
                    {currentWeights && hasAnyModifiedWeights() && (
                        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">Total Weight:</span>
                                <span className={`text-sm font-bold ${Math.abs(Object.values(currentWeights).reduce((sum, w) => sum + w, 0) - 1.0) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                                    {Math.round(Object.values(currentWeights).reduce((sum, w) => sum + w, 0) * 100)}%
                                </span>
                            </div>
                            {Math.abs(Object.values(currentWeights).reduce((sum, w) => sum + w, 0) - 1.0) >= 0.01 && (
                                <p className="text-xs text-red-600 mt-1">Weights must sum to 100%</p>
                            )}
                            {hasAnyModifiedWeights() && (
                                <div className="flex items-center gap-2 mt-2">
                                    <Clock className="w-4 h-4 text-orange-500" />
                                    <span className="text-xs text-orange-600 font-medium">
                                        Weights modified - click "Apply" to recalculate
                                    </span>
                                </div>
                            )}
                            <button
                                onClick={() => {
                                    if (currentWeights && Math.abs(Object.values(currentWeights).reduce((sum, w) => sum + w, 0) - 1.0) < 0.01) {
                                        handleApplyWeights();
                                    }
                                }}
                                disabled={isRecalculating || !currentWeights || Math.abs(Object.values(currentWeights).reduce((sum, w) => sum + w, 0) - 1.0) >= 0.01}
                                className="weight-apply-btn"
                            >
                                {isRecalculating ? '...' : 'Apply'}
                            </button>
                        </div>
                    )}

                    {/* Additional Financial Ratios */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Max Loan Amount</h4>
                            <p className="text-2xl font-bold text-gray-900">
                                {client.results?.financial_ratios.max_loan_amount ? `$${(client.results.financial_ratios.max_loan_amount).toLocaleString()}` : 'N/A'}
                            </p>
                            <p>
                                Formula: Operating Income × 5
                            </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Loan Interest Rate</h4>
                            <p className="text-2xl font-bold text-gray-900">
                                {client.results?.financial_ratios.loan_interest_rate ? `${(client.results.financial_ratios.loan_interest_rate * 100).toFixed(2)}%` : 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Warnings */}
            {creditScore.warnings && creditScore.warnings.length > 0 && (
                <div className="card bg-yellow-50 border-yellow-200">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-yellow-600 mt-1" />
                        <div>
                            <h3 className="font-semibold text-yellow-900 mb-2">Warnings</h3>
                            <ul className="space-y-1">
                                {creditScore.warnings.map((warning, index) => (
                                    <li key={index} className="text-yellow-800">• {warning}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Detailed Analysis</h3>

                {/* Tabs */}
                <div className="flex gap-4 mb-6 border-b">
                    {(['computation', 'trends', 'source data', 'equipment', 'documents'] as const).map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`pb-2 px-1 font-medium capitalize ${activeTab === tab
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                            type="button"
                        >
                            {tab === 'computation' ? 'Computation Details' : tab}
                        </button>
                    ))}
                </div>

                {activeTab === 'computation' && creditScore.computation_details && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Detailed Computation Analysis</h4>
                            <p className="text-sm text-gray-600 mb-6">
                                Explore how each category score was calculated from the source financial data.
                                Click on any category below to see the detailed computation breakdown.
                            </p>
                            <div className="space-y-4">
                                {Object.entries(creditScore.computation_details).map(([category, computation]) => {
                                    const categoryColors = {
                                        liquidity: { bg: 'bg-blue-50', text: 'text-blue-900', icon: 'text-blue-600' },
                                        profitability: { bg: 'bg-green-50', text: 'text-green-900', icon: 'text-green-600' },
                                        leverage: { bg: 'bg-red-50', text: 'text-red-900', icon: 'text-red-600' },
                                        efficiency: { bg: 'bg-orange-50', text: 'text-orange-900', icon: 'text-orange-600' },
                                        cash_flow: { bg: 'bg-purple-50', text: 'text-purple-900', icon: 'text-purple-600' }
                                    };
                                    const colors = categoryColors[category as keyof typeof categoryColors] || categoryColors.liquidity;

                                    // Create ref for this category if it doesn't exist
                                    if (!computationRefs.current[category]) {
                                        computationRefs.current[category] = React.createRef<HTMLDivElement>();
                                    }

                                    return (
                                        <ComputationDetails
                                            key={category}
                                            category={category}
                                            computation={computation}
                                            categoryColor={colors}
                                            isFocused={focusedComputationCategory === category}
                                            onFocusChange={(focused) => {
                                                if (!focused) {
                                                    setFocusedComputationCategory(null);
                                                }
                                            }}
                                            scrollRef={computationRefs.current[category]}
                                        />
                                    );
                                })}
                            </div>
                            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <h5 className="font-medium text-gray-900 mb-2">Understanding the Computation</h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• <strong>Source Value:</strong> The actual financial ratio calculated from your data</li>
                                    <li>• <strong>Benchmark:</strong> Industry-specific reference value for comparison</li>
                                    <li>• <strong>Normalized Score:</strong> 0-100 score based on how your ratio compares to the benchmark</li>
                                    <li>• <strong>Weight:</strong> How much this ratio contributes to the category score</li>
                                    <li>• <strong>Weighted Score:</strong> Normalized score multiplied by the weight</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'trends' && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-blue-600" />
                                Financial Ratio Trends
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                View historical trends of key financial ratios across multiple years.
                                Upload financial statements for multiple years to see comprehensive trend analysis.
                            </p>

                            {/* Ratio Selector */}
                            <div className="mb-6">
                                <label htmlFor="ratio-selector" className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Financial Ratio
                                </label>
                                <select
                                    id="ratio-selector"
                                    value={selectedRatio}
                                    onChange={(e) => setSelectedRatio(e.target.value)}
                                    className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {availableRatios.map(ratio => (
                                        <option key={ratio.key} value={ratio.key}>
                                            {ratio.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Trend Chart */}
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                {isLoadingTrends ? (
                                    <div className="flex items-center justify-center h-96">
                                        <div className="text-center">
                                            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
                                            <p className="text-gray-600">Loading trend data...</p>
                                        </div>
                                    </div>
                                ) : (
                                    <D3TrendChart
                                        data={trendData}
                                        selectedRatio={availableRatios.find(r => r.key === selectedRatio)?.label || selectedRatio}
                                    />
                                )}
                            </div>

                            {/* Information Box */}
                            {trendData.length === 0 && (
                                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                                        <div>
                                            <h5 className="font-medium text-blue-900 mb-1">No Trend Data Available</h5>
                                            <p className="text-sm text-blue-800">
                                                To view trends, upload financial statements for multiple years (e.g., 2022, 2023, 2024).
                                                The system will automatically extract the year from your filenames or PDF content.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {trendData.length > 0 && (
                                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                    <h5 className="font-medium text-gray-900 mb-2">Trend Summary</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                        <li>• <strong>Years Analyzed:</strong> {trendData.map(d => d.year).join(', ')}</li>
                                        <li>• <strong>Latest Value:</strong> {trendData[trendData.length - 1].value.toFixed(3)}</li>
                                        {trendData.length > 1 && (
                                            <>
                                                <li>• <strong>Change:</strong>
                                                    {(trendData[trendData.length - 1].value - trendData[0].value).toFixed(3)}
                                                    ({(((trendData[trendData.length - 1].value - trendData[0].value) / trendData[0].value) * 100).toFixed(1)}%)
                                                </li>
                                                <li>• <strong>Trend:</strong> {
                                                    trendData[trendData.length - 1].value > trendData[0].value ? '🔺 Improving' : '🔻 Declining'
                                                }</li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'source data' && (
                    <UnifiedFileUpload clientId={client?.id} />
                )}

                {activeTab === 'equipment' && (
                    <div className="space-y-6">
                        <EquipmentSelector clientId={client?.id} />
                        <EquipmentList clientId={client?.id} />
                    </div>
                )}

                {activeTab === 'documents' && (
                    <div className="space-y-6">
                        <DocumentUpload clientId={client?.id} />

                        {/* Parsed Documents Display */}
                        {client?.id && (
                            <ParsedDocumentsDisplay
                                clientId={client.id}
                                parsedDocuments={parsedDocuments}
                                isLoading={isLoadingParsedDocuments}
                                onRefresh={() => {
                                    if (client?.id) {
                                        setIsLoadingParsedDocuments(true);
                                        getParsedDocuments(client.id)
                                            .then(response => {
                                                if (response.success && response.parsed_documents) {
                                                    setParsedDocuments(response.parsed_documents);
                                                }
                                            })
                                            .catch(error => {
                                                console.error('Failed to fetch parsed documents:', error);
                                            })
                                            .finally(() => {
                                                setIsLoadingParsedDocuments(false);
                                            });
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

interface ParsedDocumentsDisplayProps {
    clientId: string;
    parsedDocuments: {
        equifax: ParsedDocument[];
        paynet: ParsedDocument[];
        id: ParsedDocument[];
    };
    isLoading: boolean;
    onRefresh: () => void;
}

const ParsedDocumentsDisplay: React.FC<ParsedDocumentsDisplayProps> = ({
    parsedDocuments,
    isLoading,
    onRefresh
}) => {
    const hasAnyParsedDocuments = parsedDocuments.equifax.length > 0 ||
        parsedDocuments.paynet.length > 0 ||
        parsedDocuments.id.length > 0;

    if (!hasAnyParsedDocuments && !isLoading) {
        return null;
    }

    return (
        <div className="card">
            <div className="card-header">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h2 className="card-title">Parsed Documents</h2>
                    </div>
                    <button
                        onClick={onRefresh}
                        disabled={isLoading}
                        className="btn btn-secondary btn-sm"
                        type="button"
                    >
                        {isLoading ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Loading...
                            </>
                        ) : (
                            <>
                                <RefreshCw className="w-4 h-4" />
                                Refresh
                            </>
                        )}
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="p-8 text-center">
                    <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
                    <p className="text-gray-600">Loading parsed documents...</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Equifax Reports */}
                    {parsedDocuments.equifax.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">Equifax Reports</h3>
                            <div className="space-y-4">
                                {parsedDocuments.equifax.map((doc) => (
                                    <EquifaxDisplay key={doc.file_id} doc={doc} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Paynet Reports */}
                    {parsedDocuments.paynet.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">Paynet Reports</h3>
                            <div className="space-y-4">
                                {parsedDocuments.paynet.map((doc) => (
                                    <PaynetDisplay key={doc.file_id} doc={doc} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ID Documents */}
                    {parsedDocuments.id.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-3">ID Documents</h3>
                            <div className="space-y-4">
                                {parsedDocuments.id.map((doc) => (
                                    <IDDisplay key={doc.file_id} doc={doc} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface DocumentDisplayProps {
    doc: ParsedDocument;
}

const EquifaxDisplay: React.FC<DocumentDisplayProps> = ({ doc }) => {
    const data = doc.parsed_data;
    return (
        <div className="border rounded-lg p-4 bg-blue-50">
            <h4 className="font-medium text-gray-900 mb-3">{doc.file_name}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Credit Score</p>
                    <p className="text-2xl font-bold text-gray-900">{data.credit_score}</p>
                    <p className="text-sm text-gray-500">Rating: {data.credit_rating}</p>
                </div>
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Credit Utilization</p>
                    <p className="text-xl font-bold text-gray-900">{data.credit_utilization?.utilization_percentage}%</p>
                    <p className="text-sm text-gray-500">
                        ${data.credit_utilization?.total_credit_used?.toLocaleString()} / ${data.credit_utilization?.total_credit_limit?.toLocaleString()}
                    </p>
                </div>
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Payment History</p>
                    <p className="text-lg font-semibold text-gray-900">
                        {data.payment_history?.on_time_payments} on-time payments
                    </p>
                    <p className="text-sm text-gray-500">
                        {data.payment_history?.late_payments_30 || 0} late (30 days), {data.payment_history?.late_payments_60 || 0} late (60 days)
                    </p>
                </div>
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Accounts</p>
                    <p className="text-lg font-semibold text-gray-900">
                        {data.payment_history?.open_accounts} open, {data.payment_history?.closed_accounts} closed
                    </p>
                    <p className="text-sm text-gray-500">Total: {data.payment_history?.total_accounts}</p>
                </div>
            </div>
            {data.account_details && data.account_details.length > 0 && (
                <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Account Details</p>
                    <div className="space-y-2">
                        {data.account_details.map((account: any, idx: number) => (
                            <div key={idx} className="bg-white p-2 rounded text-sm">
                                <span className="font-medium">{account.account_name}</span> - {account.account_type}
                                <span className="ml-2 text-gray-600">
                                    ${account.balance?.toLocaleString()} / ${account.credit_limit?.toLocaleString()}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const PaynetDisplay: React.FC<DocumentDisplayProps> = ({ doc }) => {
    const data = doc.parsed_data;
    return (
        <div className="border rounded-lg p-4 bg-green-50">
            <h4 className="font-medium text-gray-900 mb-3">{doc.file_name}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Paynet Score</p>
                    <p className="text-2xl font-bold text-gray-900">{data.paynet_score}</p>
                    <p className="text-sm text-gray-500">Payment Index: {data.payment_index}</p>
                </div>
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Payment Trend</p>
                    <p className="text-xl font-bold text-gray-900">{data.payment_trend}</p>
                    <p className="text-sm text-gray-500">On-time: {data.payment_history?.on_time_percentage}%</p>
                </div>
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Payment History</p>
                    <p className="text-lg font-semibold text-gray-900">
                        {data.payment_history?.on_time_payments} / {data.payment_history?.total_payments} on-time
                    </p>
                    <p className="text-sm text-gray-500">
                        Late: {data.payment_history?.late_payments_1_30 || 0} (1-30), {data.payment_history?.late_payments_31_60 || 0} (31-60)
                    </p>
                </div>
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Industry Comparison</p>
                    <p className="text-lg font-semibold text-gray-900">
                        {data.industry_comparison?.percentile_rank}th percentile
                    </p>
                    <p className="text-sm text-gray-500">Industry avg: {data.industry_comparison?.industry_average}</p>
                </div>
            </div>
            {data.trade_references && data.trade_references.length > 0 && (
                <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Trade References</p>
                    <div className="space-y-2">
                        {data.trade_references.map((trade: any, idx: number) => (
                            <div key={idx} className="bg-white p-2 rounded text-sm">
                                <span className="font-medium">{trade.creditor_name}</span> - {trade.account_type}
                                <span className="ml-2 text-gray-600">
                                    ${trade.current_balance?.toLocaleString()} / ${trade.highest_credit?.toLocaleString()}
                                </span>
                                <span className="ml-2 text-gray-500">({trade.months_reported} months)</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const IDDisplay: React.FC<DocumentDisplayProps> = ({ doc }) => {
    const data = doc.parsed_data;
    return (
        <div className="border rounded-lg p-4 bg-purple-50">
            <h4 className="font-medium text-gray-900 mb-3">{doc.file_name}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Document Type</p>
                    <p className="text-lg font-semibold text-gray-900">{data.document_type}</p>
                </div>
                <div className="bg-white p-3 rounded">
                    <p className="text-sm text-gray-600">Verification Status</p>
                    <p className={`text-lg font-semibold ${data.verification?.document_valid ? 'text-green-600' : 'text-red-600'}`}>
                        {data.verification?.document_valid ? 'Valid' : 'Invalid'}
                    </p>
                    <p className="text-sm text-gray-500">Score: {(data.verification?.verification_score * 100).toFixed(0)}%</p>
                </div>
            </div>
            {data.extracted_info && (
                <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Extracted Information</p>
                    <div className="bg-white p-3 rounded space-y-1 text-sm">
                        <p><span className="font-medium">Name:</span> {data.extracted_info.full_name}</p>
                        <p><span className="font-medium">Date of Birth:</span> {data.extracted_info.date_of_birth}</p>
                        <p><span className="font-medium">Address:</span> {data.extracted_info.address}</p>
                        {data.extracted_info.license_number && (
                            <p><span className="font-medium">License Number:</span> {data.extracted_info.license_number}</p>
                        )}
                        {data.extracted_info.expiration_date && (
                            <p><span className="font-medium">Expiration:</span> {data.extracted_info.expiration_date}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientDetails;
